/**
 * Variant Limiter
 * 
 * Limits product variant purchases based on a metafield value.
 * Add a metafield to variants named 'theme.variant_limit' with a number value.
 * When inventory meets or exceeds this limit, the variant becomes unavailable.
 */

class VariantLimiter {
  constructor() {
    this.variantLimitMap = {};
    this.variantStockMap = {};
    //console.log('[VariantLimiter] Initializing...');
    this.init();
  }

  init() {
    // Get all product forms on the page
    const forms = document.querySelectorAll('[data-product-form]');
    //console.log(`[VariantLimiter] Found ${forms.length} product form(s)`);
    forms.forEach((form, index) => {
      //console.log(`[VariantLimiter] Setting up form ${index + 1}`);
      this.setupForm(form);
    });
  }

  setupForm(form) {
    // Parse variant limits from form data
    const variantLimitsStr = form.getAttribute('data-variant-limits');
    //console.log(`[VariantLimiter] variantLimitsStr: "${variantLimitsStr}"`);
    
    if (!variantLimitsStr) return; // No limits defined, nothing to do

    if (variantLimitsStr) {
      this.parseVariantLimits(variantLimitsStr);
    }
    const variantStockStr = form.getAttribute('data-variant-stock');
    //console.log(`[VariantLimiter] variantStockStr: "${variantStockStr}"`);
    if (variantStockStr) {
      this.parseVariantStock(variantStockStr);
    }

    // Listen for variant changes
    const variantSelectors = form.querySelectorAll('[data-single-option-selector]');
    //console.log(`[VariantLimiter] Found ${variantSelectors.length} variant selector(s)`);
    variantSelectors.forEach((selector, index) => {
      selector.addEventListener('change', () => {
        //console.log(`[VariantLimiter] Variant selector ${index} changed`);
        // Add a delay to allow the form's variant calculation to complete
        setTimeout(() => {
          //console.log(`[VariantLimiter] Checking variant state after selector change (delayed)`);
          this.updateVariantState(form);
        }, 100);
      });
    });

    // Initial state check
    //console.log(`[VariantLimiter] Checking initial variant state`);
    setTimeout(() => {
        this.updateVariantState(form);
    }, 100);
  }

  parseVariantLimits(limitsStr) {
    // Format: "variantId1:limitValue1,variantId2:limitValue2,"
    if (!limitsStr) {
      //console.log('[VariantLimiter] No variant limits string provided');
      return;
    }
    
    const pairs = limitsStr.split(',').filter(p => p);
    //console.log(`[VariantLimiter] Parsing ${pairs.length} variant limit(s): ${limitsStr}`);
    pairs.forEach(pair => {
      const [variantId, limit] = pair.split(':');
      if (variantId && limit) {
        this.variantLimitMap[variantId] = parseInt(limit, 10);
        //console.log(`[VariantLimiter] Set limit for variant ${variantId}: ${limit}`);
      }
    });
    //console.log('[VariantLimiter] Final variantLimitMap:', this.variantLimitMap);
  }

  parseVariantStock(stockStr) {
    // Format: "variantId1:limitValue1,variantId2:limitValue2,"
    if (!stockStr) {
      return;
    }
    
    const pairs = stockStr.split(',').filter(p => p);
    //console.log(`[VariantLimiter] Parsing ${pairs.length} variant stock(s): ${stockStr}`);
    pairs.forEach(pair => {
      const [variantId, stock] = pair.split(':');
      if (variantId && stock) {
        this.variantStockMap[variantId] = parseInt(stock, 10);
        //console.log(`[VariantLimiter] Set stock for variant ${variantId}: ${stock}`);
      }
    });
    //console.log('[VariantLimiter] Final variantStockMap:', this.variantStockMap);
  }

  updateVariantState(form) {
    // Get the currently selected variant ID
    const variantInput = form.querySelector('[data-product-select]');
    if (!variantInput) {
      //console.log('[VariantLimiter] Could not find variant input');
      return;
    }

    const currentVariantId = variantInput.value;
    const limit = this.variantLimitMap[currentVariantId];
    
    //console.log(`[VariantLimiter] updateVariantState - currentVariantId: ${currentVariantId}, limit: ${limit}`);
    //console.log(`[VariantLimiter] variantInput value: "${variantInput.value}", variantLimitMap:`, this.variantLimitMap);

    // Check if this variant has a limit
    if (limit !== undefined) {
      //console.log(`[VariantLimiter] Variant ${currentVariantId} has a limit of ${limit}, checking inventory...`);
      // Fetch current inventory for this variant
      this.checkVariantInventory(currentVariantId, limit, form);
    } else {
      //console.log(`[VariantLimiter] Variant ${currentVariantId} has no limit, enabling button`);
      // No limit on this variant, enable button
      this.setButtonState(form, true);
    }
  }

  checkVariantInventory(variantId, limit, form) {
    const stock = this.variantStockMap[variantId];
    
    if (stock === undefined) {
        //console.log(`[VariantLimiter] No stock data for variant ${variantId}, enabling button`);
        this.setButtonState(form, true);
        return;
    }

    const isLimited = stock <= limit;
    //console.log(`[VariantLimiter] isLimited: ${isLimited} (stock ${stock} >= limit ${limit})`);
    this.setButtonState(form, !isLimited);
    this.updateSwatchState(form, variantId, !isLimited);
}


  setButtonState(form, isEnabled) {
    const button = form.querySelector('[data-add-to-cart]');
    //console.log(`[VariantLimiter] setButtonState - isEnabled: ${isEnabled}, button found: ${!!button}`);
    if (button) {
      const shouldDisable = !isEnabled;
      button.disabled = shouldDisable;
      //console.log(`[VariantLimiter] Set button.disabled = ${shouldDisable}, actual button.disabled now = ${button.disabled}`);
      
      if (!isEnabled) {
        button.classList.add('btn--disabled');
        //console.log('[VariantLimiter] Added btn--disabled class, classes now:', button.className);
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text) {
          text.textContent = 'Limit Reached';
          //console.log('[VariantLimiter] Set button text to "Limit Reached"');
        }
      } else {
        button.classList.remove('btn--disabled');
        //console.log('[VariantLimiter] Removed btn--disabled class, classes now:', button.className);
        // Reset to original text
        // this.resetButtonText(button, form);
      }
    } else {
      //console.log('[VariantLimiter] ERROR: Could not find button with [data-add-to-cart] selector');
      //console.log('[VariantLimiter] Form element:', form);
      //console.log('[VariantLimiter] All buttons in form:', form.querySelectorAll('button'));
    }
  }

  updateSwatchState(form, variantId, isEnabled) {
    // Find swatches that contain this variant and disable them if limit reached
    const swatches = form.querySelectorAll('[data-swatch-variant]');
    //console.log(`[VariantLimiter] updateSwatchState - variantId: ${variantId}, isEnabled: ${isEnabled}, swatches found: ${swatches.length}`);
    swatches.forEach((swatch, index) => {
      const variantIds = swatch.getAttribute('data-swatch-variant')?.split(',') || [];
      const containsVariant = variantIds.includes(variantId.toString());
      //console.log(`[VariantLimiter] Swatch ${index}: variantIds=[${variantIds}], containsVariant=${containsVariant}`);
      
      if (containsVariant) {
        const input = swatch.closest('[data-swatches-button]')?.querySelector('input');
        if (input) {
          input.disabled = !isEnabled;
          //console.log(`[VariantLimiter] Swatch ${index}: Set input.disabled = ${!isEnabled}`);
          if (!isEnabled) {
            swatch.closest('[data-swatches-button]').classList.add('swatch--disabled');
            //console.log(`[VariantLimiter] Swatch ${index}: Added swatch--disabled class`);
          } else {
            swatch.closest('[data-swatches-button]').classList.remove('swatch--disabled');
            //console.log(`[VariantLimiter] Swatch ${index}: Removed swatch--disabled class`);
          }
        }
      }
    });
  }
}

// Initialize variant limiter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantLimiter();
  });
} else {
  new VariantLimiter();
}
